import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { isDesktop } from '../../app/desktop';
import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { brand } from '../../config/brand';
import { api } from '../../lib/api';
import { useDocumentTitle } from '../../lib/title';
import { type LoginResponse, useSession } from '../../stores/session';
import { AuthLayout } from './AuthLayout';
import { takeVerifier } from './desktopHandoff';

/**
 * `/desktop-return` - one route, reached twice, by two different programs.
 *
 * ## In the browser: hand the code to the app
 *
 * This is where the system browser lands after a desktop sign-in. It holds a
 * one-time code that is worth nothing here and everything in the app, so its
 * whole job is to open the app's URL scheme and then get out of the way.
 *
 * ## In the app: spend it
 *
 * The shell answers that scheme by pointing its own window at this same route,
 * which is why there is no second page and no message passing between the main
 * process and this one. `isDesktop` is the entire difference, and it comes from
 * the preload bridge rather than from anything in the URL - so a link somebody
 * is sent cannot make a browser behave as though it were the app.
 *
 * ## Why the code alone is not enough
 *
 * The claim also presents the verifier the app kept before it opened the
 * browser. A code read out of a process list, a shell history or a browser's
 * address bar cannot be spent without it. See `desktopHandoff`.
 */
export function DesktopReturnPage() {
  useDocumentTitle('Signing you in');

  const { search } = useLocation();
  const navigate = useNavigate();
  const adopt = useSession((state) => state.adopt);

  const code = new URLSearchParams(search).get('code') ?? '';
  const target = `${brand.desktopScheme}://auth?code=${encodeURIComponent(code)}`;

  const [failed, setFailed] = useState(false);
  const [handedOver, setHandedOver] = useState(false);

  useEffect(() => {
    if (code === '') {
      setFailed(true);

      return;
    }

    if (!isDesktop) {
      /*
       * Assigning rather than opening a window: the app's scheme is not a page,
       * and a browser that has no handler for it does nothing visible either
       * way. The button below is the fallback for exactly that.
       */
      window.location.href = target;
      setHandedOver(true);

      return;
    }

    const verifier = takeVerifier();

    if (verifier === null) {
      setFailed(true);

      return;
    }

    void api
      .post<LoginResponse>('/auth/desktop/claim', { code, verifier }, { anonymous: true })
      .then((session) => {
        adopt(session);
        navigate('/', { replace: true });
      })
      .catch(() => setFailed(true));
  }, [code, target, adopt, navigate]);

  if (failed) {
    return (
      <AuthLayout
        title="That did not go through"
        subtitle="These sign-ins are good for two minutes, and only once."
        footer={
          <>
            Try again from <Link to="/login" className="text-accent hover:underline">the sign-in page</Link>.
          </>
        }
      >
        <Alert tone="error">
          Nothing was created and nothing was lost. Pressing Continue with Discord again starts a fresh one.
        </Alert>
      </AuthLayout>
    );
  }

  if (isDesktop) {
    return (
      <AuthLayout title="Signing you in" subtitle="One moment.">
        <p className="text-sm text-soft">Finishing up.</p>
      </AuthLayout>
    );
  }

  /*
   * Not "you are signed in", which would be a claim about this browser.
   *
   * For somebody who already had an account, this tab holds no session at all -
   * the callback minted a code instead of setting a cookie, precisely so the
   * session could end up in the app. Telling them they are signed in here would
   * be wrong, and it would be wrong in the direction that makes them close the
   * app and carry on in the browser.
   */
  return (
    <AuthLayout
      title="Back to the app"
      subtitle={`Your sign-in is on its way to the ${brand.name} app. You can close this tab.`}
    >
      <div className="flex flex-col gap-4">
        {handedOver && <Alert tone="success">Handed over. Look for the app window.</Alert>}

        {/*
          The fallback, and it is not rare enough to hide. A browser will refuse
          to open an application scheme that no program has registered, and will
          also refuse one it thinks the page did not deserve - both silently. A
          button the person presses themselves is treated differently, because
          it is a gesture rather than a redirect.
        */}
        <Button onClick={() => { window.location.href = target; }} full>
          Open the app
        </Button>

        <p className="text-center text-[13px] leading-relaxed text-faint">
          Nothing happening? Make sure the app is installed and running, then press the button again.
        </p>
      </div>
    </AuthLayout>
  );
}
